// Chapters (rebuild.md §4.9): 17 cards with poster thumbnail, chapter number and title, stage name, FILED
// stamp, commendations on the current tier, lock state, and a download-for-offline toggle with its size.
import { useEffect, useState } from 'preact/hooks';
import { isFiled, isUnlocked, save, tier } from '@app/state.ts';
import { CHAPTERS } from '@content/chapters.ts';
import { hasStage } from '@content/stages/index.ts';
import { loadMediaManifest, mediaManifest, mediaUrl } from '@media/manifest.ts';
import { downloadAll, downloadPack, formatBytes, freeSpace, packs, refreshPackStates, removePack } from '@media/packs.ts';
import { Button, Stamp } from '@ui/core.tsx';
import { Screen, useNavigate } from './Shell.tsx';

export function Chapters() {
  const navigate = useNavigate();
  const [free, setFree] = useState<number | null>(null);
  useEffect(() => {
    void loadMediaManifest().then(() => refreshPackStates());
    void freeSpace().then(setFree);
  }, []);
  const m = mediaManifest.value;
  const t = tier.value;
  const totalBytes = Object.values(m.packs).reduce((s, p) => s + p.bytes, 0);
  return (
    <Screen
      title="Chapters"
      actions={
        totalBytes > 0 && (
          <Button tone="ghost" onClick={() => void downloadAll(CHAPTERS.filter((c) => isUnlocked(c.id)).map((c) => c.id))}>
            Download all ({formatBytes(totalBytes)})
          </Button>
        )
      }
    >
      {free !== null && totalBytes > 0 && <p class="storage-bar">Free storage on this device: about {formatBytes(free)}.</p>}
      {/* role="list" restores list semantics that Safari drops for list-style: none */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul class="chapter-grid" role="list">
        {CHAPTERS.map((c) => {
          const unlocked = isUnlocked(c.id);
          const playable = unlocked && c.built && hasStage(c.id);
          const result = save.value.results[c.id]?.[t];
          const filed = isFiled(c.id);
          const pre = m.excerpts.find((e) => e.stage === c.id && e.role === 'pre');
          const thumb = mediaUrl(pre?.thumb ?? null);
          const pack = m.packs[c.id];
          const ps = packs.value[c.id];
          return (
            <li key={c.id} class={`chapter-card${unlocked ? '' : ' locked'}`}>
              <div class="chapter-thumb">
                {thumb && unlocked ? <img src={thumb} alt="" loading="lazy" /> : <span aria-hidden="true">{unlocked ? '' : '🔒'}</span>}
                <span class="chapter-num">{c.label}</span>
                {filed && <Stamp text="FILED" />}
              </div>
              <div class="chapter-info">
                <p class="chapter-title">{c.title}</p>
                <h2 class="chapter-stage">{c.stage}</h2>
                <p class="chapter-summary">{c.summary}</p>
              </div>
              <div class="chapter-foot">
                <span class="chapter-stars" aria-label={`${result ? result.commendations.filter(Boolean).length : 0} of 3 commendations on this tier`}>
                  {[0, 1, 2].map((i) => (result?.commendations[i] ? '★' : '☆')).join('')}
                </span>
                {pack && unlocked && (
                  <span class="chapter-dl">
                    {ps?.status === 'downloading' ? (
                      `Downloading… ${Math.round((ps.done / Math.max(1, ps.total)) * 100)}%`
                    ) : ps?.status === 'ready' ? (
                      <button type="button" class="btn btn-ghost" onClick={() => void removePack(c.id)} aria-label={`Remove the offline download of ${c.label}`}>
                        ✔ Offline{ps.stale ? ' (update)' : ''}
                      </button>
                    ) : (
                      <button type="button" class="btn btn-ghost" onClick={() => void downloadPack(c.id)} aria-label={`Download ${c.label} for offline (${formatBytes(pack.bytes)})`}>
                        ⤓ {formatBytes(pack.bytes)}
                      </button>
                    )}
                    {ps?.status === 'error' && <span role="alert"> {ps.message}</span>}
                  </span>
                )}
                {playable ? (
                  <Button tone={filed ? 'secondary' : 'primary'} class="chapter-play" onClick={() => navigate(`/stage/${c.id}`)} aria-label={`Play ${c.label}: ${c.stage}`}>
                    {filed ? 'Replay' : 'Play'}
                  </Button>
                ) : (
                  <span class="inprod chapter-play">{!unlocked ? 'Locked' : 'In production'}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Screen>
  );
}
